import { initializeOBFeedback } from '@coherence-design-system/controls/lib/OfficeBrowserFeedback';
import { Link, Stack } from '@fluentui/react';
import { useLoginOnStartup } from '@micro-frontend-react/employee-experience/lib/useLoginOnStartup';
import React, { useContext } from 'react';
import { render } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { CoherenceThemeProvider } from './Components/CoherenceThemeProvider/CoherenceThemeProvider';
import { ILoginOptions } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import { IUser } from '@micro-frontend-react/employee-experience/lib/IUser';
import { Footer } from './Components/Footer';
import { Header } from './Components/Header';
import { LoadingSpinner } from './Components/LoadingSpinner/LoadingSpinner';
import { Main } from './Components/Main';
import { Navigation } from './Components/Nav';
import { setNav } from './navConfig';
import { Routes } from './Routes';
import { ShellWithStore } from './ShellWithStore';
import { useHeaderConfig } from './useHeaderConfig';
import { useHistory } from 'react-router-dom';
import { INavLink } from '@coherence-design-system/controls/lib/nav';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { userRolesReducer, userRolesReducerName, userRolesInitialState } from '././Redux/UserRoles/reducer';
import { acquireToken, processTokenRoles } from './Services/TokenService';
import { IUserRoles } from './Interfaces/IUserRoles';
import { setUserRoles, clearUserRoles } from './Redux/UserRoles/action';
import { IUserRolesAppState } from "././Redux";
import { ISatReportAppState } from '././Redux/index';
import { requestReportList } from '././Redux/Reports/action';
import { reportsInitialState, reportsReducer, reportsReducerName } from '././Redux/Reports/reducer';
import { requestReportListSagas  } from './Services/ReportService';

initializeOBFeedback(
    1111,
    'running-environment',
    '/ocv/OfficeBrowserFeedback.min.js',
    '/ocv/OfficeBrowserFeedback.min.css',
    '/ocv/intl/',
    'https://office365.uservoice.com/'
);

function App(): React.ReactElement {
    const loginOptions: ILoginOptions = { scopes: __SCOPES__ };
    const [loggedInUser] = useLoginOnStartup(true, loginOptions) as [IUser | null];
    const headerConfig = useHeaderConfig();
    const [isNavCollapsed, setIsNavCollapsed] = React.useState<boolean>(false);
    const history = useHistory();
    const { authClient } = useContext(Context as React.Context<IEmployeeExperienceContext>);
    const { useSelector, dispatch } = useContext(Context as React.Context<IEmployeeExperienceContext>);
    const [token, setToken] = React.useState<string | null>(null);

  // Add reports reducer to fetch report list
  useDynamicReducer(reportsReducerName, reportsReducer as any, [requestReportListSagas]);
  const reportsState = useSelector((state: ISatReportAppState) => state.dynamic?.[reportsReducerName] || reportsInitialState);

  //Fetch report list request
  React.useEffect(() => {
    dispatch(requestReportList());
  }, [dispatch]);

    //get token useEffect 
    React.useEffect(() => {
        acquireToken(authClient, setToken, dispatch, clearUserRoles);
    }, [authClient, dispatch]);


  //get roles from token and combine report list role(old sat application procedure based)
    React.useEffect(() => {
    if (token) {
        const reportListData = reportsState?.data && Array.isArray(reportsState.data)
          ? reportsState.data
          : null;
        processTokenRoles(token, dispatch, setUserRoles, clearUserRoles, reportListData);
    }
  }, [token, reportsState?.data, dispatch]);

    useDynamicReducer(userRolesReducerName, userRolesReducer, []);
    const userRolesState = useSelector((state: IUserRolesAppState) => state.dynamic?.[userRolesReducerName] || userRolesInitialState);
    const roles: IUserRoles | null = userRolesState.roles;

    const handleLinkClick = (ev: React.MouseEvent<HTMLElement>, item: INavLink): void => {
        ev.preventDefault();
        ev.stopPropagation();
        history.push(item.url || item.href);
    };
    const navConfig = setNav(handleLinkClick);

    if (!loggedInUser || !roles) {
        return <LoadingSpinner/>;
    }

    function _onNavCollapsed(isCollapsed?: boolean) {
        if (isCollapsed !== undefined && isCollapsed !== null) {
            setIsNavCollapsed(isCollapsed);
        }
    };

    return (
        <>
            <Header
                {...headerConfig}
            />
            <Navigation
                groups={navConfig}
                appName={'Severance Administration Tool'}
                defaultIsNavCollapsed={isNavCollapsed}
                onNavCollapsed={_onNavCollapsed}
            />
            <Main
                className={isNavCollapsed ? 'navCollapsed' : 'navExpanded'}
                id="main"
                tabIndex={-1}
            >
                <Routes/>
            </Main>
            <Footer
                className={isNavCollapsed ? 'navCollapsed' : 'navExpanded'}
            >
                <Stack
                    horizontal
                    tokens={{ childrenGap: 10 }}
                >
                    <Link
                        href="https://msdpn.azurewebsites.net"
                        target="_blank"
                        styles={{ root: { color: '0078d4', backgroundColor: 'ffffff' } }}
                    >
                        Data Protection Notice
                    </Link>
                    <Link
                        href="https://microsoft.sharepoint.com/sites/hrw"
                        target="_blank"
                        styles={{ root: { color: '0078d4', backgroundColor: 'ffffff' } }}
                    >
                        Help
                    </Link>
                </Stack>
            </Footer>
        </>
    );
}

render(
    // @ts-ignore
    <ShellWithStore>
        <CoherenceThemeProvider>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </CoherenceThemeProvider>
    </ShellWithStore>,
    document.getElementById('app')
);
